/* eslint-disable operator-linebreak */
/* eslint-disable max-lines-per-function */
import {Button, CircularProgress, Grid, Typography} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {makeStyles, Theme} from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import SignIcon from '@material-ui/icons/Create';
import PolicyIcon from '@material-ui/icons/Policy';
import Alert from '@material-ui/lab/Alert';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import locale from '../locale/Localization';
import {beginSign, endSign, signVerify} from '../script/sign';
import {
  CONSENT_CODE_CHARS,
  REGULAR_EXPRESSION_MOBILE_NUMBER,
  URL_MANUAL_DE,
  URL_MANUAL_FR
} from '../utils/constants';
import {
  BeginSignRequest,
  EndSignRequest,
  SignResult,
  SignVerifyRequest
} from '../utils/types';

const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {paddingBottom: 0},
  gridContainer: {margin: theme.spacing(2, 1)},
  container: {display: 'flex', flexWrap: 'wrap'},
  button: {margin: '1em 0 1.5em 0', align: 'left'},
  mobileNumberField: {
    marginRight: theme.spacing(1),
    marginLeft: -6,
    width: '100%'
  }
}));

const renderManualButton = () => {
  const getUrlByLang = (de: string, fr: string) => {
    const lang = locale.getLanguage();
    return lang === 'de' ? de : fr;
  };
  return (
    <Tooltip placement='bottom' title={locale.manualDownloadButtonTooltip}>
      <Button
        variant='text'
        color='primary'
        style={{
          marginTop: '0.5em',
          marginLeft: '0.8em',
          textDecoration: 'underline'
        }}
        onClick={() => window.open(getUrlByLang(URL_MANUAL_DE, URL_MANUAL_FR))}
      >
        {locale.manualDownloadButton}
      </Button>
    </Tooltip>
  );
};

const newRandomConsentCode = () => {
  let code = '';

  while (code.length < 6) {
    const digit = Math.floor(Math.random() * 100) % 10;
    code += CONSENT_CODE_CHARS[digit];
  }

  return code;
};

const renderSignatureDeclarationButton = (applicationId: string | number) => {
  const url = '/registration/' + applicationId + '/signed-document';
  return (
    <Tooltip
      placement={'top'}
      title={locale.registrationUploadDialogActionDownloadTooltip}
    >
      <Button
        variant='contained'
        color='primary'
        onClick={() => window.open(url, '_blank')}
      >
        {locale.registrationUploadDialogActionDownload}
      </Button>
    </Tooltip>
  );
};

/**
 * Customized dialog for Swisscom Signing.
 *
 * @param {*} props - Dialog attributes passed as property.
 * @return {React.Fragment} - The component content.
 */
export default function SwisscomSignDialog(props) {
  const classes = useStyles();

  const [mobileNumber, setMobileNumber] = useState();
  const [validatedField, setValidatedField] = useState(true);
  const [evidence, setEvidence] = useState(false);
  const [evidenceError, setEvidenceError] = useState(false);
  const [consentCode, setConsentCode] = useState('');
  const [signed, setSigned] = useState(false);
  const [signError, setSignError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [pending, setPending] = useState(false);

  const validateField = async (value: string) => {
    await setValidatedField(value.match(REGULAR_EXPRESSION_MOBILE_NUMBER));
  };

  const handleFieldChange = async (event: Event) => {
    event.persist();
    const value = event.target.value.replaceAll(/\s/g, '');
    await setMobileNumber(value);
    await validateField(value);
  };

  const handleSignVerifyRequest = async (request: SignVerifyRequest) => {
    try {
      const response = await signVerify(request);

      if (response.ok) {
        setEvidence(true);
      } else {
        setEvidenceError(true);
      }
    } catch (error) {
      setEvidenceError(true);
    }
  };

  const handleError = (error: any) => {
    setPending(false);
    setSignError(true);
    setErrorMessage(error);
  };

  const endSignPolling = async (request: EndSignRequest) => {
    try {
      const result = await endSign(request);

      if (!processSignResult(result)) {
        startEndSignPolling(request);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const startEndSignPolling = (request: EndSignRequest) => {
    setTimeout(() => endSignPolling(request), 1000);
  };

  const handleSignRequest = async () => {
    const code = newRandomConsentCode();
    setConsentCode(code);
    setSignError(false);
    setErrorMessage();
    setPending(true);

    try {
      const request: BeginSignRequest = {
        notaryApplicationId: props.applicationId,
        consentId: code
      };
      const result = await beginSign(request);

      if (!processSignResult(result)) {
        const endSignRequest: EndSignRequest = {
          notaryApplicationId: props.applicationId,
          taskId: result.taskId
        };
        startEndSignPolling(endSignRequest);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const processSignResult = (result: SignResult) => {
    if (result.status === 'SUCCESS') {
      setPending(false);
      setSigned(true);
      props.handleSuccess();
      return true;
    }

    if (result.status === 'PENDING') {
      return false;
    }

    throw new Error(result.error);
  };

  const renderSwisscomValidateButton = () => {
    const request: SignVerifyRequest = {
      notaryApplicationId: props.applicationId,
      mobilePhoneNumber: mobileNumber
    };

    return (
      <Button
        disabled={!validatedField || !mobileNumber}
        variant='contained'
        color='primary'
        onClick={() => {
          handleSignVerifyRequest(request);
        }}
        endIcon={<PolicyIcon />}
      >
        {locale.registrationSwisscomDialogActionValidate}
      </Button>
    );
  };

  const renderSwisscomSignButton = () => {
    return (
      <Button
        disabled={!evidence}
        disableElevation={evidence && pending}
        disableRipple={evidence && pending}
        variant='contained'
        color='primary'
        onClick={() => handleSignRequest()}
        endIcon={
          pending ? (
            <CircularProgress color='secondary' size={23} />
          ) : (
            <SignIcon />
          )
        }
      >
        {locale.registrationSwisscomDialogActionSign}
      </Button>
    );
  };

  const renderMobileNumberfield = () => {
    return (
      <TextField
        fullwidth='true'
        variant='outlined'
        disabled={evidence}
        error={!validatedField}
        helperText={!validatedField ? locale.invalidMobileNumber : ''}
        placeholder={locale.mobilNumberPlaceHolder}
        margin='dense'
        value={mobileNumber}
        name='mobileNumber'
        label={locale.mobileNumber}
        type='text'
        className={classes.mobileNumberField}
        onChange={(event) => {
          handleFieldChange(event);
        }}
      />
    );
  };

  const renderSwisscomStatusRow = () => {
    const SEVERITY_INFO = 'info';
    const SEVERITY_SUCCESS = 'success';
    const SEVERITY_ERROR = 'error';
    let statusMessage = '';
    let severity = '';

    // Handles validation result
    if (evidence && !signed) {
      statusMessage = locale.registrationSwisscomDialogStatusEvidenceSuccess;
      severity = SEVERITY_SUCCESS;
    } else if (!evidence && evidenceError) {
      statusMessage = locale.registrationSwisscomDialogStatusEvidenceError;
      severity = SEVERITY_ERROR;
    }

    // Handles signing process
    if (evidence && pending) {
      statusMessage = locale.signingCodeSubmitMsg + consentCode;
      severity = SEVERITY_INFO;
    }

    // Handles sign result
    if (signed) {
      statusMessage = props.registration
        ? locale.registrationSwisscomDialogStatusSignSuccess
        : locale.signatureTemplateSignCompletedSwisscom;
      severity = SEVERITY_SUCCESS;
    } else if (signError) {
      statusMessage =
        locale.registrationSwisscomDialogStatusSignError + errorMessage;
      severity = SEVERITY_ERROR;
    }

    return (
      <>
        {statusMessage && (
          <Grid
            xs={12}
            item
            style={{paddingTop: 18, marginLeft: -8, paddingBottom: 5}}
          >
            <Typography variant='body1' component='span'>
              <Alert severity={severity}>{statusMessage}</Alert>
            </Typography>
          </Grid>
        )}
      </>
    );
  };

  const renderSwisscomSignDialog = () => {
    return (
      <Dialog
        disableBackdropClick
        aria-labelledby='Signature declaration sign'
        onClose={props.handleClose}
        open={props.open}
      >
        <DialogTitle
          id='signature-declaration-sign-dialog-title'
          onClose={props.handleClose}
        >
          {locale.registrationSwisscomDialogTitle}
        </DialogTitle>
        {!signed && (
          <DialogContent dividers className={classes.dialogContent}>
            <Typography gutterBottom>
              {locale.registrationSwisscomDialogDescriptionValidation}
            </Typography>
            <Typography gutterBottom>
              {locale.registrationSwisscomDialogDescriptionManual}
            </Typography>
            <Grid container spacing={0} className={classes.gridContainer}>
              <Grid xs={6} item>
                {renderMobileNumberfield()}
              </Grid>
              <Grid xs={6} item>
                {renderManualButton()}
              </Grid>
              {renderSwisscomStatusRow()}
            </Grid>
          </DialogContent>
        )}
        {signed && (
          <DialogContent dividers style={{paddingTop: 0, paddingBottom: 10}}>
            {renderSwisscomStatusRow()}
          </DialogContent>
        )}
        <DialogActions>
          {/* UNVERIFIED ACTIONS */}
          {!signed && (
            <Button
              onClick={props.handleClose}
              variant='outlined'
              disabled={pending}
            >
              {locale.cancelButton}
            </Button>
          )}
          {!evidence && renderSwisscomValidateButton()}
          {/* VERIFIED ACTIONS */}
          {!signed && evidence && renderSwisscomSignButton()}
          {/* SIGNED ACTIONS */}
          {signed && (
            <Button
              onClick={props.handleClose}
              variant='outlined'
              color='primary'
            >
              {locale.signerDownloadDialogButtonClose}
            </Button>
          )}
          {signed &&
            props.applicationId &&
            renderSignatureDeclarationButton(props.applicationId)}
        </DialogActions>
      </Dialog>
    );
  };

  useEffect(() => {
    if (!mobileNumber) {
      setMobileNumber(props.mobileNumber);
    }
  }, [props.open]);

  return (
    <>
      {renderSwisscomSignDialog()}
      <Tooltip
        placement='top'
        title={locale.registrationButtonSignSwisscomTooltip}
      >
        <Button
          variant='contained'
          color='primary'
          className={props.buttonStyle ?? classes.button}
          onClick={props.handleOpen}
          endIcon={<SignIcon />}
        >
          {locale.registrationButtonSignSwisscom}
        </Button>
      </Tooltip>
    </>
  );
}

SwisscomSignDialog.propTypes = {
  applicationId: PropTypes.number,
  open: PropTypes.bool.isRequired,
  buttonStyle: PropTypes.string,
  handleOpen: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSuccess: PropTypes.func.isRequired,
  mobileNumber: PropTypes.string.isRequired,
  registration: PropTypes.bool.isRequired
};
